// [AI 툴 카드 컴포넌트] 개별 AI 도구 정보 표시 - 이름, 설명, BEST 뱃지, 북마크, 평점, 링크 버튼

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AITool } from '../types';
import { handleImageError } from '../utils/imageMapping';
import { apiService } from '../services';

interface ToolCardProps {
  tool: AITool;
  rank?: number;
  className?: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, rank, className }) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [statusCheckAttempts, setStatusCheckAttempts] = useState(0);
  const [scaleFactor, setScaleFactor] = useState(1);
  const cardRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleCardNavigation = () => {
    if (tool.id) {
      navigate(`/tool/${tool.id}`);
    }
  };

  // MyPage인지 확인
  const isMyPage = location.pathname === '/mypage';

  // 카드 너비 측정 및 scale factor 계산
  useEffect(() => {
    const updateScale = () => {
      if (cardRef.current) {
        const width = cardRef.current.offsetWidth;
        const baseWidth = 326; // 기준 너비
        setScaleFactor(width / baseWidth);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    
    // ResizeObserver로 카드 자체 크기 변화 감지
    const resizeObserver = new ResizeObserver(updateScale);
    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateScale);
      resizeObserver.disconnect();
    };
  }, []);

  // 인증 상태 및 북마크 상태 확인 (페이지 변경 시마다 실행)
  useEffect(() => {
    const checkAuthAndBookmark = async () => {
      console.log('🔄 ToolCard 상태 확인 시작:', { toolId: tool.id, attempts: statusCheckAttempts });
      
      const authenticated = apiService.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (authenticated && tool.id) {
        try {
          const numericId = parseInt(tool.id);
          if (!isNaN(numericId)) {
            console.log('🔍 북마크 상태 확인 중:', numericId);
            const bookmarkStatus = await apiService.checkBookmarkStatus(numericId);
            setIsBookmarked(bookmarkStatus);
            console.log('✅ 북마크 상태 설정 완료:', bookmarkStatus);
          }
        } catch (error) {
          console.warn('⚠️ 북마크 상태 확인 실패:', error);
          
          if (statusCheckAttempts < 2) {
            console.log('🔄 북마크 상태 재확인 시도');
            setTimeout(() => {
              setStatusCheckAttempts(prev => prev + 1);
            }, 1000);
          }
        }
      } else {
        setIsBookmarked(false);
      }
    };

    checkAuthAndBookmark();
  }, [tool.id, statusCheckAttempts, location.pathname]);

  // 북마크 토글 핸들러
  const handleBookmarkToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔄 북마크 토글 시작:', { 
      toolId: tool.id, 
      isAuthenticated, 
      currentBookmarkState: isBookmarked 
    });
    
    if (!isAuthenticated) {
      alert('로그인이 필요한 기능입니다.');
      return;
    }

    if (bookmarkLoading) {
      console.log('⏳ 이미 처리 중...');
      return;
    }

    const numericId = parseInt(tool.id);
    if (isNaN(numericId)) {
      console.error('❌ 유효하지 않은 tool.id:', tool.id);
      alert('도구 ID가 올바르지 않습니다.');
      return;
    }

    try {
      setBookmarkLoading(true);
      
      // 현재 실제 서버 상태를 다시 한 번 확인
      console.log('🔍 서버에서 현재 북마크 상태 재확인');
      const currentServerState = await apiService.checkBookmarkStatus(numericId);
      console.log('📊 서버 상태 vs 로컬 상태:', { 
        server: currentServerState, 
        local: isBookmarked 
      });
      
      // 서버 상태와 로컬 상태가 다른 경우 동기화
      if (currentServerState !== isBookmarked) {
        console.log('🔄 상태 동기화 중...');
        setIsBookmarked(currentServerState);
      }
      
      // 실제 서버 상태에 따라 액션 결정
      if (currentServerState) {
        console.log('🗑️ 북마크 제거 시도');
        await apiService.removeBookmark(numericId);
        setIsBookmarked(false);
        console.log('✅ 북마크 제거 완료');
        
        // MyPage에서 북마크 해제 시 페이지 새로고침 (UI에서 카드가 사라지도록)
        if (isMyPage) {
          window.location.reload();
        }
      } else {
        console.log('➕ 북마크 추가 시도');
        await apiService.addBookmark(numericId);
        setIsBookmarked(true);
        console.log('✅ 북마크 추가 완료');
      }
      
    } catch (error) {
      console.error('❌ 북마크 처리 실패:', error);
      
      let errorMessage = '북마크 처리 중 오류가 발생했습니다.';
      
      if (error instanceof Error) {
        if (error.message.includes('이미 북마크')) {
          errorMessage = '이미 북마크된 서비스입니다.';
          setIsBookmarked(true);
        } else if (error.message.includes('존재하지 않는') || error.message.includes('북마크가 없')) {
          errorMessage = '북마크가 존재하지 않습니다.';
          setIsBookmarked(false);
        } else if (error.message.includes('로그인')) {
          errorMessage = '로그인이 필요합니다.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(errorMessage);
      
      // 에러 발생 후 상태 재확인
      try {
        const updatedState = await apiService.checkBookmarkStatus(numericId);
        setIsBookmarked(updatedState);
      } catch (recheckError) {
        console.warn('상태 재확인 실패:', recheckError);
      }
      
    } finally {
      setBookmarkLoading(false);
      console.log('🏁 북마크 토글 완료');
    }
  };

  // 외부 URL로 이동하는 핸들러
  const handleExternalLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (tool.url && tool.url.trim() !== '') {
      // URL이 http:// 또는 https://로 시작하지 않으면 https:// 추가
      let url = tool.url.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      console.log('🔗 외부 링크로 이동:', url);
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('⚠️ 유효하지 않은 URL:', tool.url);
      alert('유효한 웹사이트 URL이 없습니다.');
    }
  };

  // scale 함수: 기준값 * scale factor
  const scale = (baseValue: number) => `${baseValue * scaleFactor}px`;

  // BEST 뱃지
  const getBestBadge = (rank: number) => {
    return (
      <span className="inline-flex items-center"
            data-best-badge
            style={{ 
              backgroundColor: '#F2EEFB', 
              color: '#7E50D1',
              borderRadius: scale(8),
              justifyContent: 'center',
              paddingTop: scale(5.5),
              paddingBottom: scale(5.5),
              paddingLeft: scale(10),
              paddingRight: scale(10),
              fontWeight: 600,
              fontSize: scale(14),
              lineHeight: '150%',
              letterSpacing: '0.007em',
              fontFamily: 'Pretendard'
            }}>
        BEST {rank}
      </span>
    );
  };

  // DB tags 컬럼을 우선적으로 표시하는 함수
  const getDisplayTag = () => {
    // 1순위: tags가 배열인 경우 첫 번째 요소 사용 (DB tags 컬럼 내용)
    if (Array.isArray(tool.tags) && tool.tags.length > 0) {
      const firstTag = tool.tags[0];
      if (firstTag && firstTag !== '') {
        return firstTag;
      }
    }
    
    // 2순위: tags가 문자열인 경우 (이전 버전 호환성)
    if (typeof tool.tags === 'string' && tool.tags !== '') {
      return tool.tags;
    }
    
    // 3순위: categoryLabel을 fallback으로 사용 (기존 로직 유지)
    return tool.categoryLabel || 'AI 도구';
  };

  return (
    <div 
         ref={cardRef}
         className={`bg-white rounded-xl group ${className || ''}`} 
         style={{ 
           border: '1px solid #DBCBF9', 
           fontFamily: 'Pretendard', 
           padding: scale(20),
           aspectRatio: '326 / 245',
           cursor: 'pointer',
           display: 'flex',
           flexDirection: 'column',
           overflow: 'hidden',
           width: '100%'
         }}
         onClick={handleCardNavigation}
         onMouseEnter={(e) => {
           e.currentTarget.style.backgroundColor = '#F2EEFB';
          e.currentTarget.style.border = '1px solid #DBCBF9';
          e.currentTarget.style.outline = '1px solid #F2EEFB';
          e.currentTarget.style.outlineOffset = '-1px';
           const tagsElement = e.currentTarget.querySelector('[data-tags]');
           if (tagsElement) {
             (tagsElement as HTMLElement).style.backgroundColor = '#DBCBF9';
           }
           const visitButton = e.currentTarget.querySelector('[data-visit-button]');
           if (visitButton) {
             (visitButton as HTMLElement).style.backgroundColor = '#7E50D1';
             const visitIcon = visitButton.querySelector('img');
             if (visitIcon) {
               (visitIcon as HTMLImageElement).src = '/images/Icon/Visit/32/White.svg';
             }
           }
           const bestBadge = e.currentTarget.querySelector('[data-best-badge]');
           if (bestBadge) {
             (bestBadge as HTMLElement).style.backgroundColor = '#E9DFFB';
           }
         }}
         onMouseLeave={(e) => {
           e.currentTarget.style.backgroundColor = '#FFFFFF';
           e.currentTarget.style.border = '1px solid #DBCBF9';
          e.currentTarget.style.outline = 'none';
           const tagsElement = e.currentTarget.querySelector('[data-tags]');
           if (tagsElement) {
             (tagsElement as HTMLElement).style.backgroundColor = '#E9DFFB';
           }
           const visitButton = e.currentTarget.querySelector('[data-visit-button]');
           if (visitButton) {
             (visitButton as HTMLElement).style.backgroundColor = '#E9DFFB';
             const visitIcon = visitButton.querySelector('img');
             if (visitIcon) {
               (visitIcon as HTMLImageElement).src = '/images/Icon/Visit/32/Black.svg';
             }
           }
           const bestBadge = e.currentTarget.querySelector('[data-best-badge]');
           if (bestBadge) {
             (bestBadge as HTMLElement).style.backgroundColor = '#F2EEFB';
           }
         }}>
      
      {/* 1번째 줄: 로고, BEST 뱃지, 바로가기 아이콘 */}
      <div className="flex items-start justify-between" style={{ margin: 0, padding: 0 }}>
        {/* 로고 */}
        <div className="flex-shrink-0 flex items-center justify-center" 
             style={{ 
               width: scale(70),
               height: scale(70),
               margin: 0, 
               padding: 0 
             }}>
          {tool.logoUrl ? (
            <img 
              src={tool.logoUrl} 
              alt={`${tool.name} 로고`}
              style={{ 
                width: 'auto', 
                height: 'auto', 
                maxWidth: scale(70),
                maxHeight: scale(70),
                objectFit: 'contain' 
              }}
              onError={(e) => handleImageError(e, '/images/Logo/Logo_FINAL.svg')}
            />
          ) : (
            <span style={{ fontSize: scale(40) }}>🤖</span>
          )}
        </div>

        {/* 오른쪽: BEST 뱃지, 북마크 버튼 (MyPage에서만), 바로가기 버튼 */}
        <div className="flex items-start flex-shrink-0" 
             style={{ 
               gap: scale(10),
               margin: 0, 
               padding: 0 
             }}>
          {/* BEST 뱃지 */}
          {rank && rank <= 3 && getBestBadge(rank)}

          {/* 북마크 버튼 - MyPage에서만 표시 */}
          {isMyPage && isAuthenticated && (
            <button
              onClick={handleBookmarkToggle}
              disabled={bookmarkLoading}
              className={`flex items-center justify-center ${
                bookmarkLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ 
                backgroundColor: isBookmarked ? '#E9DFFB' : '#F3F4F6', 
                width: scale(40),
                height: scale(40),
                borderRadius: scale(3.56),
                margin: 0,
                padding: 0
              }}
              title={isBookmarked ? '북마크 해제' : '북마크 추가'}
              onMouseEnter={(e) => {
                if (!bookmarkLoading) {
                  e.currentTarget.style.backgroundColor = '#DBCBF9';
                }
              }}
              onMouseLeave={(e) => {
                if (!bookmarkLoading) {
                  e.currentTarget.style.backgroundColor = isBookmarked ? '#E9DFFB' : '#F3F4F6';
                }
              }}
            >
              {bookmarkLoading ? (
                <div className="border-2 border-current border-t-transparent rounded-full animate-spin" 
                     style={{ width: scale(20), height: scale(20) }} />
              ) : (
                <img src="/images/Icon/Save/Filled/32/Purple_Filled.svg" 
                     alt="북마크" 
                     style={{ width: scale(24), height: scale(24) }} />
              )}
            </button>
          )}

          {/* 바로가기 버튼 - 외부 URL로 이동 */}
          <button
            onClick={handleExternalLink}
            className="flex items-center justify-center" 
            data-visit-button
            style={{ 
              backgroundColor: '#E9DFFB', 
              width: scale(32),
              height: scale(32),
              borderRadius: scale(8),
              padding: 0,
              margin: 0
            }}
            title={`${tool.name} 공식 사이트로 이동`}
          >
            <img src="/images/Icon/Visit/32/Black.svg" 
                 alt="바로가기" 
                 style={{ width: scale(32), height: scale(32) }} />
          </button>
        </div>
      </div>

      {/* 2번째 줄: 카테고리 뱃지 */}
      <div style={{ 
        marginTop: scale(8),
        marginBottom: 0, 
        padding: 0 
      }}>
        <span className="inline-flex items-center" 
              data-tags
              style={{ 
                backgroundColor: '#E9DFFB',
                borderRadius: scale(100),
                color: '#202020',
                paddingTop: scale(2),
                paddingBottom: scale(2),
                paddingLeft: scale(10),
                paddingRight: scale(10),
                fontWeight: 400,
                fontSize: scale(14),
                lineHeight: '150%',
                letterSpacing: '0.007em',
                width: 'fit-content',
                margin: 0
              }}>
          {getDisplayTag()}
        </span>
      </div>

      {/* 3번째 줄: AI명 */}
      <div style={{ 
        marginTop: scale(12),
        marginBottom: 0, 
        padding: 0 
      }}>
        <h3 style={{ 
              fontWeight: 500,
              fontSize: scale(24),
              lineHeight: '150%',
              letterSpacing: '0.007em',
              margin: 0,
              padding: 0,
              color: '#000000',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
          {tool.name}
        </h3>
      </div>

      {/* 4번째 줄: AI 설명 */}
      <div style={{ 
        marginTop: scale(6),
        marginBottom: 0, 
        padding: 0, 
        height: scale(48),
        overflow: 'hidden' 
      }}>
        <p style={{ 
            color: '#202020',
            fontWeight: 500,
            fontSize: scale(16),
            lineHeight: scale(24),
            letterSpacing: '0.007em',
            margin: 0,
            padding: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
          {tool.description}
        </p>
      </div>
    </div>
  );
};

export default ToolCard;